import { clearToken, getRefreshToken, getToken, setSession } from '../auth/session';

const BASE = `${import.meta.env.VITE_API_BASE_URL}/api/v1`;

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/**
 * Renew the access token with the refresh token. A single in-flight refresh is
 * shared across concurrent 401s so we don't rotate the refresh token twice.
 */
let refreshing: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (r) => {
        if (!r.ok) return false;
        const data = (await r.json()) as { accessToken: string; refreshToken: string };
        setSession(data.accessToken, data.refreshToken);
        return true;
      })
      .catch(() => false)
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

/** Typed fetch wrapper. Attaches the bearer token and handles auth/errors. */
export async function api<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 401) {
    // The short-lived access token likely expired: renew it once and retry.
    // Only a genuinely dead session (refresh also fails) logs the user out.
    if (token && retry && (await tryRefresh())) {
      return api<T>(path, init, false);
    }
    if (token) {
      clearToken();
      window.location.reload();
    }
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = body.message ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Uploads a file (multipart) and returns its public URL (absolute). */
export async function apiUpload(file: File): Promise<{ url: string; mimetype: string; size: number }> {
  const token = getToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/uploads`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    let message = res.statusText;
    try { message = (await res.json()).message ?? message; } catch { /* ignore */ }
    throw new ApiError(res.status, message);
  }
  const data = (await res.json()) as { url: string; mimetype: string; size: number };
  // The API returns a path (/uploads/…); make it absolute so <img>/<video> resolve.
  return { ...data, url: `${import.meta.env.VITE_API_BASE_URL}${data.url}` };
}

export const apiGet = <T>(path: string) => api<T>(path);
export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined });
export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });
